import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { mapInputEventName } from 'Modules/.common/utils/translateEvents';

const FIELDS = ["address", "driverName", "eventName", "latitude", "longitude", "unitTime"];

const pick = (obj, unitInputs) =>
  FIELDS.reduce((acc, key) => {
    let value = obj?.[key];
    if (key === 'eventName' && value && unitInputs?.length) {
      value = mapInputEventName(value, unitInputs) || value;
    }
    acc[key] = value == null ? "" : String(value);
    return acc;
  }, {});

const getRowsFromVehicleTrips = (vehicleTrips, messages, unitInputs) => {
  const items = vehicleTrips?.data ?? [];
  const ignitionOn = messages["realtimemaps_vehicletrails_export_event_ignition_on"] ?? "Ignition On";
  const ignitionOff = messages["realtimemaps_vehicletrails_export_event_ignition_off"] ?? "Ignition Off";
  const rows = [];
  for (const trip of items) {
    if (trip?.start) {
      rows.push({ eventType: ignitionOn, ...pick(trip.start, unitInputs) });
    }
    if (trip?.end) {
      rows.push({ eventType: ignitionOff, ...pick(trip.end, unitInputs) });
    }
  }
  return rows;
};

const escapeCsvCell = (value) => {
  const str = String(value ?? "");
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = (rows) => {
  const headers = ["eventType", ...FIELDS];
  const headerLine = headers.map(escapeCsvCell).join(",");
  const dataLines = rows.map((row) =>
    headers.map((h) => escapeCsvCell(row[h])).join(",")
  );
  return [headerLine, ...dataLines].join("\r\n");
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const VehicleTripsExport = ({ vehicleTrips, unitInputs }) => {
  const { messages } = useIntl();

  const handleExport = useCallback(() => {
    const rows = getRowsFromVehicleTrips(vehicleTrips, messages, unitInputs);
    if (rows.length === 0) return;
    const csv = buildCsv(rows);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const baseName = messages["realtimemaps_vehicletrails_export_filename"] ?? "vehicle-trips";
    const filename = `${baseName}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadBlob(blob, filename);
  }, [vehicleTrips, messages, unitInputs]);

  const hasData = (vehicleTrips?.data?.length ?? 0) > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="subtitle1" color="text.secondary">
        {messages["realtimemaps_vehicletrails_export_title"]}
      </Typography>
      <Tooltip title={messages["realtimemaps_vehicletrails_export"]}>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={handleExport}
            disabled={!hasData}
            aria-label={messages["realtimemaps_vehicletrails_export"]}
          >
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
